import { socialExport } from '@mi-front/api-clients';
import { useMemo, useState } from 'react';
import { YoutubePlaylistsAutocomplete } from './YoutubePlaylistsAutocomplete';
import { YoutubePlaylistsRefreshLink } from './YoutubePlaylistsRefreshLink';

type Props = {
  stationId: string;
};

/**
 * Multi-select field for YouTube playlists.
 * Fetches playlists from the API and allows selecting multiple playlists.
 * Includes a clickable link in the helper text to refresh playlists from YouTube.
 */
const MAX_PLAYLISTS = 10;

export function YoutubePlaylistsField({ stationId }: Props) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const playlistsQuery = socialExport.useGetYoutubePlaylists(stationId, {
    query: {
      enabled: !!stationId,
    },
  });

  const playlists = playlistsQuery.data?.data ?? [];
  const isLoading = playlistsQuery.isLoading || isRefreshing;

  // Sort playlists by title for better UX
  const sortedPlaylists = useMemo(() => {
    return [...playlists].sort((a, b) => a.title.localeCompare(b.title));
  }, [playlists]);

  const helperText = (
    <YoutubePlaylistsRefreshLink
      stationId={stationId}
      isLoading={isLoading}
      onRefreshStateChange={setIsRefreshing}
    />
  );

  return (
    <YoutubePlaylistsAutocomplete
      playlists={sortedPlaylists}
      isLoading={isLoading}
      maxSelected={MAX_PLAYLISTS}
      helperText={helperText}
    />
  );
}
